import type {
  CmsDestination,
  CmsKind,
  CmsLandingCarouselPageKey,
  CmsLandingHeroPageKey,
  CmsLandingPageKey,
  CmsStatus,
} from './types'

const SAFE_TAGS = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'blockquote', 'ul', 'ol',
  'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'a', 'img', 'figure', 'figcaption',
  'pre', 'code', 'span', 'div',
])

export function slugifyCms(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 320)
}

export function isHttpUrl(value: string | null | undefined): boolean {
  if (!value) return false
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function isPublicPath(value: string | null | undefined): boolean {
  return Boolean(value?.startsWith('/') && !value.startsWith('//'))
}

export function isCmsMediaUrl(value: string | null | undefined): boolean {
  return isHttpUrl(value) || isPublicPath(value)
}

/** Conservative allow-list sanitizer for trusted editorial HTML and Webflow imports. */
export function sanitizeCmsHtml(input: string | null | undefined): string {
  if (!input) return ''
  return input
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|iframe|object|embed|template|svg|math)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<\/?([a-zA-Z0-9-]+)([^>]*)>/g, (match, rawTag: string, rawAttrs: string) => {
      const tag = rawTag.toLowerCase()
      if (!SAFE_TAGS.has(tag)) return ''
      if (match.startsWith('</')) return `</${tag}>`

      const attrs: string[] = []
      const attrPattern = /([a-zA-Z:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g
      let attrMatch: RegExpExecArray | null
      while ((attrMatch = attrPattern.exec(rawAttrs))) {
        const name = attrMatch[1].toLowerCase()
        const value = attrMatch[2] ?? attrMatch[3] ?? ''
        if (name.startsWith('on') || name === 'style') continue
        if (tag === 'a' && name === 'href' && isHttpUrl(value)) attrs.push(`href="${escapeAttribute(value)}"`)
        if (tag === 'a' && name === 'target' && value === '_blank') attrs.push('target="_blank" rel="noopener noreferrer"')
        if (tag === 'img' && name === 'src' && isHttpUrl(value)) attrs.push(`src="${escapeAttribute(value)}"`)
        if (tag === 'img' && (name === 'alt' || name === 'title')) attrs.push(`${name}="${escapeAttribute(value)}"`)
      }
      return `<${tag}${attrs.length ? ` ${attrs.join(' ')}` : ''}>`
    })
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

export function parseCmsKind(value: unknown): CmsKind | null {
  return value === 'learn' ||
    value === 'blog' ||
    value === 'demo' ||
    value === 'landing_carousel' ||
    value === 'landing_hero'
    ? value
    : null
}

/** baby: carousel page selector: does not include /nin hero-only key */
export function parseCmsLandingCarouselPageKey(
  value: unknown,
): CmsLandingCarouselPageKey | null {
  return value === 'ninxl' || value === 'ningenie_home' ? value : null
}

/** baby: fixed platform demo button pages (/nin + /ninxl) */
export function parseCmsLandingHeroPageKey(value: unknown): CmsLandingHeroPageKey | null {
  return value === 'nin' || value === 'ninxl' ? value : null
}

export function parseCmsLandingPageKey(value: unknown): CmsLandingPageKey | null {
  return parseCmsLandingCarouselPageKey(value) ?? parseCmsLandingHeroPageKey(value)
}

/** Fixed slug per hero page so admin upserts stay singleton */
export function landingHeroSlugForPage(pageKey: CmsLandingHeroPageKey): string {
  return pageKey === 'nin' ? 'platform-demo-nin' : 'platform-demo-ninxl'
}

export function landingHeroTitleForPage(pageKey: CmsLandingHeroPageKey): string {
  return pageKey === 'nin' ? 'NIN platform demo' : 'NinXL platform demo'
}

export function parseCmsStatus(value: unknown): CmsStatus | null {
  return value === 'draft' || value === 'published' || value === 'archived' ? value : null
}

export function parseCmsDestination(value: unknown): CmsDestination | null {
  return value === 'internal' || value === 'external' ? value : null
}

export function validatePublishableEntry(input: {
  title?: string
  slug?: string
  status?: CmsStatus
  kind?: CmsKind
  summary?: string | null
  destination?: CmsDestination
  externalUrl?: string | null
  demoUrl?: string | null
  bodyHtml?: string | null
  pageKey?: CmsLandingPageKey | null
}): string | null {
  if (!input.title?.trim()) return 'Title is required'
  if (!slugifyCms(input.slug || input.title)) return 'A valid slug is required'
  if (input.kind === 'demo') {
    if (input.status === 'published' && !input.summary?.trim()) return 'Published demos require a summary'
    if (input.status === 'published' && !isCmsMediaUrl(input.demoUrl)) return 'Published demos require a valid demo URL'
    return null
  }
  if (input.kind === 'landing_carousel') {
    if (!parseCmsLandingCarouselPageKey(input.pageKey)) {
      return 'Landing carousel slides require a page (ninxl or ningenie_home)'
    }
    if (input.status === 'published' && !input.summary?.trim()) return 'Published landing slides require a description'
    if (input.status === 'published' && !isCmsMediaUrl(input.demoUrl)) return 'Published landing slides require a valid video URL'
    return null
  }
  if (input.kind === 'landing_hero') {
    if (!parseCmsLandingHeroPageKey(input.pageKey)) {
      return 'Landing hero demos require a page (nin or ninxl)'
    }
    if (input.status === 'published' && !isCmsMediaUrl(input.demoUrl)) {
      return 'Published landing hero demos require a valid video URL'
    }
    return null
  }
  if (input.destination === 'external' && !isHttpUrl(input.externalUrl)) return 'External entries require a valid HTTP(S) URL'
  if (input.status === 'published' && input.destination !== 'external' && !input.bodyHtml?.trim()) return 'Published internal entries require article content'
  return null
}

export function entryHref(entry: { kind: CmsKind; slug: string; destination: CmsDestination; externalUrl: string | null; pageKey?: CmsLandingPageKey | null }): string {
  if (entry.destination === 'external' && entry.externalUrl) return entry.externalUrl
  if (entry.kind === 'demo') return '/cms/demos'
  if (entry.kind === 'landing_hero') {
    return entry.pageKey === 'nin' ? '/nin' : '/ninxl'
  }
  if (entry.kind === 'landing_carousel') {
    return entry.pageKey === 'ningenie_home' ? '/ningenie/home' : '/ninxl'
  }
  return `/${entry.kind}/${entry.slug}`
}
